import React, { useEffect, Suspense, lazy } from "react";
import { HashRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthRedirectHandler from "./components/AuthRedirectHandler";
import Navbar from "./components/Navbar";
import { useApp } from "./context/AppContext";
import AppErrorBoundary from "./components/AppErrorBoundary";
import ConnectionBanner from "./components/ConnectionBanner";
import "./App.css";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also target the inner scroll container if it exists
    const scrollContainer = document.querySelector('.main-scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, [pathname]);
  return null;
};

// Lazy Load Pages for Performance
const HomePage = lazy(() => import("./pages/HomePage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const MoodTracker = lazy(() => import("./pages/MoodTracker"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const WellnessPage = lazy(() => import("./pages/WellnessPage"));
const SupportSystemPage = lazy(() => import("./pages/SupportSystemPage"));
const VoiceAIPage = lazy(() => import("./pages/VoiceAIPage"));
const FashionBuddyPage = lazy(() => import("./pages/FashionBuddyPage"));
const TwinStudioPage = lazy(() => import("./pages/TwinStudioPage"));
const TwinClonePage = lazy(() => import("./pages/TwinClonePage"));
const AestheticLabPage = lazy(() => import("./pages/AestheticLabPage"));
const VoiceAIStudio = lazy(() => import("./components/VoiceAIStudio"));
const AccountDeletion = lazy(() => import("./pages/AccountDeletion"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const PublicPrivacyPage = lazy(() => import("./pages/PublicPrivacyPage"));
const PublicTermsPage = lazy(() => import("./pages/PublicTermsPage"));
const LifeMemoPage = lazy(() => import("./pages/LifeMemoPage"));

const LoadingFallback = () => (
  <div className="twin-loader" role="status" aria-live="polite" aria-label="Initializing Twin Companion">
    <div className="twin-loader__glow twin-loader__glow--one" />
    <div className="twin-loader__glow twin-loader__glow--two" />

    <section className="twin-loader__banner">
      <div className="twin-loader__visual" aria-hidden="true">
        <span className="twin-loader__orbit twin-loader__orbit--outer" />
        <span className="twin-loader__orbit twin-loader__orbit--inner" />
        <span className="twin-loader__avatar">T</span>
        <span className="twin-loader__pulse twin-loader__pulse--one">♥</span>
        <span className="twin-loader__pulse twin-loader__pulse--two">✦</span>
      </div>

      <div className="twin-loader__copy">
        <span className="twin-loader__eyebrow">Your life, understood</span>
        <h1>Twin Companion</h1>
        <p>
          A companion that remembers what matters and helps organize your conversations,
          wellbeing, reminders, and everyday life.
        </p>
        <div className="twin-loader__features" aria-hidden="true">
          <span>Remembers you</span>
          <span>Plans with you</span>
          <span>Always nearby</span>
        </div>
      </div>
    </section>

    <div className="twin-loader__status">
      <div className="twin-loader__status-row">
        <span>Initializing your companion</span>
        <span className="twin-loader__dots"><i /><i /><i /></span>
      </div>
      <div className="twin-loader__track"><span /></div>
      <small>Preparing your private space and shared context</small>
    </div>
  </div>
);

const getStripeService = async () => {
  const { stripeService } = await import("./services/stripeService");
  return stripeService;
};

const PaymentHandler = () => {
  const { search, hash } = useLocation();
  const { dispatch } = useApp();
  const [processing, setProcessing] = React.useState(false);

  useEffect(() => {
    const getPaymentParams = () => {
      const hashSearch = window.location.hash.includes("?")
        ? window.location.hash.split("?")[1]
        : "";
      const routeHashSearch = hash.includes("?") ? hash.split("?")[1] : "";
      const params = new URLSearchParams(search || hashSearch || routeHashSearch);

      if (!params.get("session_id")) {
        const fallbackParams = new URLSearchParams(hashSearch || routeHashSearch);
        fallbackParams.forEach((value, key) => params.set(key, value));
      }

      return params;
    };

    const cleanPaymentUrl = () => {
      const cleanHash = window.location.hash.split("?")[0] || "#/";
      window.history.replaceState({}, document.title, `${window.location.pathname}${cleanHash}`);
    };

    const getSessionStatus = (sessionId) => {
      if (!sessionId) return null;
      return window.sessionStorage.getItem(`twinheart:checkout:${sessionId}`);
    };

    const setSessionStatus = (sessionId, status) => {
      if (!sessionId) return;
      const key = `twinheart:checkout:${sessionId}`;
      if (status) {
        window.sessionStorage.setItem(key, status);
      } else {
        window.sessionStorage.removeItem(key);
      }
    };

    const handlePaymentRedirects = async () => {
      // In HashRouter, search params might be in the hash: #/?credits=success&session_id=...
      const params = getPaymentParams();
      
      const creditStatus = params.get("credits");
      const isCreditSuccess = creditStatus === "success";
      const isCreditCancelled = creditStatus === "cancelled" || creditStatus === "canceled";
      const isSubscriptionSuccess = params.get("success") === "true";
      const isSubscriptionCancelled = params.get("cancelled") === "true" || params.get("canceled") === "true";
      const sessionId = params.get("session_id");
      const sessionStatus = getSessionStatus(sessionId);

      if (sessionStatus === "processing" || sessionStatus === "completed") {
        cleanPaymentUrl();
        return;
      }

      if (isCreditSuccess && sessionId && !processing) {
        setSessionStatus(sessionId, "processing");
        setProcessing(true);
        try {
          console.log("💳 Processing payment confirmation...");
          const stripeService = await getStripeService();
          const result = await stripeService.confirmCreditCheckoutSession(sessionId);
          const credits =
            result?.credits ??
            result?.wallet?.balance ??
            result?.user?.credits;
          
          if (Number.isFinite(credits)) {
            dispatch({ type: "SET_USER_DATA", payload: { credits } });
            dispatch({
              type: "ADD_NOTIFICATION",
              payload: {
                title: "Credits Added",
                message: `Your new balance is ${credits} credits.`,
                type: "success",
                time: "Just now",
              },
            });
            alert(`✅ Credits updated! Your new balance is ${credits} credits.`);
          } else {
            // Fallback: reload state from localstorage or notify success
            alert("✅ Payment successful! Your balance will update shortly.");
          }
          setSessionStatus(sessionId, "completed");
        } catch (error) {
          setSessionStatus(sessionId, null);
          console.error("❌ Payment confirmation failed:", error);
          alert("❌ Failed to verify payment. Please contact support.");
        } finally {
          // Clean up URL
          cleanPaymentUrl();
          setProcessing(false);
        }
      }

      if (isSubscriptionSuccess && sessionId && !processing) {
        setSessionStatus(sessionId, "processing");
        setProcessing(true);
        try {
          const stripeService = await getStripeService();
          const subscriptionStatus = await stripeService.getSubscriptionStatus();
          dispatch({
            type: "SET_USER_DATA",
            payload: {
              plan: subscriptionStatus.plan?.id || "premium",
              subscriptionActive: subscriptionStatus.isActive,
            },
          });
          alert("✅ Premium payment successful! Premium access may take a moment to sync.");
          setSessionStatus(sessionId, "completed");
        } catch (error) {
          setSessionStatus(sessionId, null);
          console.error("❌ Subscription sync failed:", error);
          alert("✅ Payment successful! Premium access will update shortly.");
        } finally {
          cleanPaymentUrl();
          setProcessing(false);
        }
      }

      if (isCreditCancelled || isSubscriptionCancelled) {
        alert("❌ Payment canceled!");
        cleanPaymentUrl();
      }
    };

    handlePaymentRedirects();
  }, [hash, search, dispatch, processing]);

  return null;
};

function App() {

  return (
    <AppErrorBoundary>
      <AuthProvider>
        <Router>
          <ConnectionBanner />
        <AuthRedirectHandler>
          <ScrollToTop />
          <PaymentHandler />
          <div className="min-h-screen bg-mesh">
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    <Route path="/account-deletion" element={<AccountDeletion />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route path="/privacy" element={<PublicPrivacyPage />} />
                    <Route path="/terms" element={<PublicTermsPage />} />
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <OnboardingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <MainApp />
                        </ProtectedRoute>
                      }
                    />
                </Routes>
            </Suspense>
          </div>
        </AuthRedirectHandler>
        </Router>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

function MainApp() {
  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden">
      <main className="main-scroll-container min-h-0 flex-1 overflow-y-auto">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/voice-studio" element={<VoiceAIStudio />} />
            <Route path="/fashion" element={<FashionBuddyPage />} />
            <Route path="/fashion-buddy" element={<FashionBuddyPage />} />
            <Route path="/mood" element={<MoodTracker />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/wellness" element={<WellnessPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/studio" element={<TwinStudioPage />} />
            <Route path="/clone" element={<TwinClonePage />} />
            <Route path="/lab" element={<AestheticLabPage />} />
            <Route path="/voice-ai" element={<VoiceAIPage />} />
            <Route path="/support-system" element={<SupportSystemPage />} />
            <Route path="/life-memos" element={<LifeMemoPage />} />
          </Routes>
        </Suspense>
      </main>
      <Navbar />
    </div>
  );
}

export default App;
